import { Vector3 } from 'three';
import { TrackDef } from './spline';
import { Centerline } from './centerline';
import { Frame, buildFrames, sampleSValues } from './frames';
import { RollKey, shortestAngleDelta, sortRollKeys } from './roll';
import { Section } from './sections';

const FRAME_SPACING = 0.5; // m

/**
 * Everything derived from the document's track: the arc-length LUT, a dense
 * field of banked RMF frames, and interpolated lookup by s. Rebuilt on edit;
 * the document itself stays the single source of truth.
 */
export class TrackRuntime {
  readonly centerline: Centerline;
  readonly frames: Frame[];
  readonly rollKeys: RollKey[];
  readonly sections: Section[];
  readonly totalLength: number;
  readonly closed: boolean;

  constructor(track: TrackDef, rollKeys: RollKey[], sections: Section[]) {
    this.centerline = new Centerline(track);
    this.rollKeys = sortRollKeys(rollKeys);
    this.sections = sections;
    this.totalLength = this.centerline.totalLength;
    this.closed = track.closed;
    this.frames = buildFrames(this.centerline, this.rollKeys, sampleSValues(this.centerline, FRAME_SPACING));
  }

  /** Interpolated frame at arc length s (positions lerped, axes renormalised). */
  frameAt(s: number, out?: Frame): Frame {
    const f = out ?? {
      s: 0,
      pos: new Vector3(),
      tan: new Vector3(),
      up: new Vector3(),
      right: new Vector3(),
      rollDeg: 0,
      railPos: new Vector3(),
    };
    const frames = this.frames;
    const n = frames.length;
    if (n === 0) return f;
    if (n === 1) return copyFrame(frames[0], f);

    const L = this.totalLength;
    // Closed: s=L is the seam *end* (same heartline as s=0) but may carry an
    // unwrapped roll / holonomy sample — use the last frame, don't wrap to 0.
    if (this.closed && L > 0 && s >= L - 1e-9) {
      return copyFrame(frames[n - 1], f);
    }
    s = this.centerline.normalizeS(s);
    const span = L / (n - 1);
    const idx = Math.min(Math.floor(s / span), n - 2);
    const t = span > 1e-12 ? (s - idx * span) / span : 0;
    const a = frames[idx];
    const b = frames[idx + 1];

    f.s = s;
    f.pos.lerpVectors(a.pos, b.pos, t);
    f.tan.lerpVectors(a.tan, b.tan, t).normalize();
    f.up.lerpVectors(a.up, b.up, t);
    f.up.addScaledVector(f.tan, -f.up.dot(f.tan)).normalize();
    f.right.crossVectors(f.tan, f.up).normalize();
    // Shortest path: −360→0 at the seam must not lerp through −180 (a full spin).
    f.rollDeg = a.rollDeg + shortestAngleDelta(a.rollDeg, b.rollDeg) * t;
    f.railPos.lerpVectors(a.railPos, b.railPos, t);
    return f;
  }
}

function copyFrame(src: Frame, dst: Frame): Frame {
  dst.s = src.s;
  dst.pos.copy(src.pos);
  dst.tan.copy(src.tan);
  dst.up.copy(src.up);
  dst.right.copy(src.right);
  dst.rollDeg = src.rollDeg;
  dst.railPos.copy(src.railPos);
  return dst;
}
